import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ApiSavedSearchUnsaved } from 'api/types/ApiSavedSearch';
import { MainStoreDispatch } from 'modules/main/store';
import { subscribeToSubsite as subscribeToSubsiteAction } from 'modules/main/store/ducks/subscribedSubsites';
import {
  saveListing as saveListingAction,
  saveSearch as saveSearchAction,
} from 'modules/main/store/ducks/userEnvironment';
import { getFirstParam, getRelativeURL, isExternalURL } from 'utils/url';
import { useTemporaryStorage } from 'utils/useTemporaryStorage';

type Props = {
  saveListing: typeof saveListingAction;
  saveSearch: typeof saveSearchAction;
  subscribeToSubsite: typeof subscribeToSubsiteAction;
};
type TemporaryStorageType = {
  redirectUrl?: string;
  listing?: {
    id: string;
    type: string;
    name: string;
  };
  subscribedSubsiteId?: string;
  savedSearch: ApiSavedSearchUnsaved;
};

function UnconnectedTemporaryStorageHandler({
  saveListing,
  saveSearch,
  subscribeToSubsite,
}: Props) {
  const navigate = useNavigate();
  const [navigated, setNavigated] = useState(false);
  const tmpId =
    typeof window !== 'undefined'
      ? getFirstParam(window.location, 'tmpId')
      : '';
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [, temporaryStorage] = useTemporaryStorage<TemporaryStorageType>(tmpId);
  useEffect(() => {
    if (temporaryStorage) {
      if (
        'redirectUrl' in temporaryStorage &&
        typeof temporaryStorage.redirectUrl === 'string'
      ) {
        setRedirectUrl(temporaryStorage.redirectUrl);
      }

      if (
        'listing' in temporaryStorage &&
        temporaryStorage.listing.id &&
        temporaryStorage.listing.type &&
        temporaryStorage.listing.name
      ) {
        saveListing(
          temporaryStorage.listing.id,
          temporaryStorage.listing.type,
          temporaryStorage.listing.name
        );
      }

      if ('subscribedSubsiteId' in temporaryStorage) {
        subscribeToSubsite(temporaryStorage.subscribedSubsiteId);
      }

      if ('savedSearch' in temporaryStorage) {
        saveSearch(temporaryStorage.savedSearch);
      }
    }
  }, [saveListing, saveSearch, subscribeToSubsite, temporaryStorage, tmpId]);
  if (redirectUrl && !navigated) {
    if (isExternalURL(redirectUrl)) {
      window.location.href = redirectUrl;
    } else {
      navigate(getRelativeURL(redirectUrl), { replace: true });
    }
    setNavigated(true);
  }
  return null;
}

const mapDispatchToProps = (dispatch: MainStoreDispatch) => ({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveListing: (listingId: any, listingType: any, listingName: any) =>
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(saveListingAction(listingId, listingType, listingName)),
  subscribeToSubsite: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subsiteId: any // TODO: Fix this the next time the file is edited.
  ) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch<any>(subscribeToSubsiteAction(subsiteId)),
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saveSearch: (search: any) => dispatch<any>(saveSearchAction(search)),
});

export const TemporaryStorageHandler = connect(
  null,
  mapDispatchToProps
)(UnconnectedTemporaryStorageHandler);
